import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {BrandLightBulbIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency} from "@pg-rp/common-types";

import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormHeader} from "../../../user/components/preferences_form/PreferencesFormHeader";
import {PreferencesForm} from "../../../user/components/PreferencesForm";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {savePreferences} from "../../state/registration_flow_slice";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
    onSkip?: () => void;
}

export const RegistrationPreferencesModal = (props: IProps) => {
    const {isOpen, onSuccess, onSkip} = props;
    const dispatch = useDispatch();
    const [postClient, {isSuccess}] = useCreateForYouClientMutation();
    const {data: userProfile} = useGetUserProfileQuery();
    const flowState = useAppSelector((state) => state.registrationFlow);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const defaultValues: IPreferencesDefaultValues = {
        regionSelector: [],
        regions: [],
        budget: maxBudget,
        rooms: {lower: "", upper: ""},
        creditPlan: false
    };

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(eventCategory);
        }
    }, [isOpen, eventCategory]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess]);

    const onSubmit = async (values: IPreferencesDefaultValues) => {
        if (!userProfile) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        hitGtmSurveyBasicSubmit(values, eventCategory);

        const regions = values.regions.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const price = values.budget && values.budget !== maxBudget ? {lower: 0, upper: values.budget} : null;

        const postData = {
            email: userProfile.email,
            name: userProfile.name,
            phone: userProfile.phone || "",
            marketing_consent: userProfile.email_marketing_consent,
            financing_services: values.creditPlan,
            regions: regions.length ? regions : [],
            rooms: rooms,
            types: [OfferType.FLAT, OfferType.HOUSE],
            price: price || {lower: 0, upper: PREFERENCES_FORM_MAX_DEFAULT},
            area: {lower: 0, upper: 0},
            loan_installment: {lower: 0, upper: 0},
            distance: 0
        };

        try {
            await postClient(postData).unwrap();

            dispatch(
                savePreferences({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const onModalClose = () => {
        hitGtmSurveyClose(eventCategory);
        onSkip?.();
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(eventCategory, value, section);
    };

    return (
        <Modal isOpen={isOpen} onModalClose={onModalClose}>
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive closeButtonVariant="rounded" className={modalStyle}>
                    <div className={contentStyle}>
                        <UserPreferencesFormProvider defaultValues={defaultValues}>
                            {(handleSubmit) => {
                                return (
                                    <PreferencesForm
                                        handleSubmit={handleSubmit(onSubmit)}
                                        header={<PreferencesFormHeader />}
                                        topFormContent={
                                            <div className={headerStyle} data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                <BrandLightBulbIcon size="3.6" wrapperSize="4.4" wrapperColor="var(--colors-primary)" />
                                                <div className={headerTextStyle}>
                                                    <Text variant="headline_5" as="p">
                                                        Potwierdź swoje preferencje
                                                    </Text>
                                                    <Text variant="body_copy_2">
                                                        Z chęcią pomożemy Ci w poszukiwaniach wymarzonej nieruchomości oraz najlepszego kredytu do sfinansowania
                                                        zakupu. Potrzebujemy doprecyzować kilka podstawowych parametrów poszukiwanej nieruchomości.
                                                    </Text>
                                                </div>
                                            </div>
                                        }
                                        currency={currency}
                                        maxBudget={maxBudget}
                                        formWrapperClassName={formWrapperClassName}
                                        onFieldInteraction={handleFieldInteraction}
                                        locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                        savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                    />
                                );
                            }}
                        </UserPreferencesFormProvider>
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalStyle = css({
    maxWidth: "840px",
    p: 0
});

const contentStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    md: {
        p: 3
    }
});

const formWrapperClassName = css({
    p: 2,
    md: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {BrandLightBulbIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency} from "@pg-rp/common-types";

import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormHeader} from "../../../user/components/preferences_form/PreferencesFormHeader";
import {PreferencesForm} from "../../../user/components/PreferencesForm";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {savePreferences} from "../../state/registration_flow_slice";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
    onSkip?: () => void;
}

export const RegistrationPreferencesModal = (props: IProps) => {
    const {isOpen, onSuccess, onSkip} = props;
    const dispatch = useDispatch();
    const [postClient, {isSuccess}] = useCreateForYouClientMutation();
    const {data: userProfile} = useGetUserProfileQuery();
    const flowState = useAppSelector((state) => state.registrationFlow);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const defaultValues: IPreferencesDefaultValues = {
        regionSelector: [],
        regions: [],
        budget: maxBudget,
        rooms: {lower: "", upper: ""},
        creditPlan: false
    };

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(eventCategory);
        }
    }, [isOpen, eventCategory]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess]);

    const onSubmit = async (values: IPreferencesDefaultValues) => {
        if (!userProfile) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        hitGtmSurveyBasicSubmit(values, eventCategory);

        const regions = values.regions.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const price = values.budget && values.budget !== maxBudget ? {lower: 0, upper: values.budget} : null;

        const postData = {
            email: userProfile.email,
            name: userProfile.name,
            phone: userProfile.phone || "",
            marketing_consent: userProfile.email_marketing_consent,
            financing_services: values.creditPlan,
            regions: regions.length ? regions : [],
            rooms: rooms,
            types: [OfferType.FLAT, OfferType.HOUSE],
            price: price || {lower: 0, upper: PREFERENCES_FORM_MAX_DEFAULT},
            area: {lower: 0, upper: 0},
            loan_installment: {lower: 0, upper: 0},
            distance: 0
        };

        try {
            await postClient(postData).unwrap();

            dispatch(
                savePreferences({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const onModalClose = () => {
        hitGtmSurveyClose(eventCategory);
        onSkip?.();
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(eventCategory, value, section);
    };

    return (
        <Modal isOpen={isOpen} onModalClose={onModalClose}>
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive closeButtonVariant="rounded" className={modalStyle}>
                    <div className={contentStyle}>
                        <UserPreferencesFormProvider defaultValues={defaultValues}>
                            {(handleSubmit) => {
                                return (
                                    <PreferencesForm
                                        handleSubmit={handleSubmit(onSubmit)}
                                        header={<PreferencesFormHeader />}
                                        topFormContent={
                                            <div className={headerStyle} data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                <BrandLightBulbIcon size="3.6" wrapperSize="4.4" wrapperColor="var(--colors-primary)" />
                                                <div className={headerTextStyle}>
                                                    <Text variant="headline_5" as="p">
                                                        Potwierdź swoje preferencje
                                                    </Text>
                                                    <Text variant="body_copy_2">
                                                        Z chęcią pomożemy Ci w poszukiwaniach wymarzonej nieruchomości oraz najlepszego kredytu do sfinansowania
                                                        zakupu. Potrzebujemy doprecyzować kilka podstawowych parametrów poszukiwanej nieruchomości.
                                                    </Text>
                                                </div>
                                            </div>
                                        }
                                        currency={currency}
                                        maxBudget={maxBudget}
                                        formWrapperClassName={formWrapperClassName}
                                        onFieldInteraction={handleFieldInteraction}
                                        locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                        savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                    />
                                );
                            }}
                        </UserPreferencesFormProvider>
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalStyle = css({
    maxWidth: "840px",
    p: 0
});

const contentStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    md: {
        p: 3
    }
});

const formWrapperClassName = css({
    p: 2,
    md: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
